import React, { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { InputText, PhoneWithPrefixInput } from 'src/components';
import { SelectWithSearch } from 'src/components/selects';
import { CircleLoader } from 'src/components/spinners';
import { AllowedPhoneCountries } from 'src/consts';
import { FormsErrorParser } from 'src/containers/errors';
import { ClientFormInputWithToAgencyInput } from 'src/types';
import { FooterActions } from '../FooterActions';
import { useCreateClientFormContainer } from '../hooks/useCreateClientFormContainer';

type Props = {
  testID?: string;
  requiredToAgency: boolean;
  onSubmit: (data: ClientFormInputWithToAgencyInput) => void;
  onFieldsUpdate: (fields: ClientFormInputWithToAgencyInput) => void;
  agencies: { label: string; value: any }[];
  defaultValues: Partial<ClientFormInputWithToAgencyInput>;
  onPreview: () => void;
  children?: React.ReactNode;
  isPending?: boolean;
  requiredLegal?: boolean;
  country?: (typeof AllowedPhoneCountries)[number];
};

export const CreateClientFormContainer: React.FC<Props> = ({
  requiredToAgency,
  requiredLegal,
  children,
  onSubmit,
  onFieldsUpdate,
  agencies,
  onPreview,
  defaultValues,
  isPending = false,
  testID = 'CreateClientFormContainer',
  country,
}) => {
  const { control, errors, handleSubmit, watch } = useCreateClientFormContainer<ClientFormInputWithToAgencyInput>({
    defaultValues,
    requiredToAgency,
    requiredLegal,
  });

  useEffect(() => {
    const subscription = watch((value, { name, type }) =>
      onFieldsUpdate(value as unknown as ClientFormInputWithToAgencyInput),
    );
    return () => subscription.unsubscribe();
  }, [watch, onFieldsUpdate]);

  return (
    <div data-testid={testID} className="">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="firstName"
          control={control}
          render={({ field: { onBlur, onChange, value } }) => {
            return (
              <InputText
                placeholder="forms.firstName"
                error={errors?.firstName}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
              />
            );
          }}
        />

        <Controller
          name="lastName"
          control={control}
          render={({ field: { onBlur, onChange, value } }) => {
            return (
              <InputText
                placeholder="forms.lastName"
                error={errors?.lastName}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
              />
            );
          }}
        />

        <div>
          <Controller
            name="tel"
            control={control}
            render={({ field: { onBlur, onChange, value } }) => {
              return (
                <PhoneWithPrefixInput
                  placeholder="forms.phone"
                  error={errors?.tel}
                  onChange={onChange}
                  country={country ?? 'ma'}
                  onBlur={onBlur}
                  value={value}
                />
              );
            }}
          />
        </div>
        <Controller
          name="email"
          control={control}
          render={({ field: { onBlur, onChange, value } }) => {
            return (
              <InputText
                placeholder="forms.email"
                type="email"
                error={errors?.email}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
              />
            );
          }}
        />

        {requiredToAgency && (
          <div>
            <Controller
              name="toAgency"
              control={control}
              render={({ field: { onBlur, onChange, value } }) => {
                return (
                  <SelectWithSearch
                    placeholder="estore-select-to-agency"
                    options={agencies}
                    onChange={onChange}
                    selectedValue={value}
                    hasError={!!errors?.toAgency}
                  />
                );
              }}
            />
            {!!errors?.toAgency && (
              <div className="my-1">
                <FormsErrorParser error={errors?.toAgency} />
              </div>
            )}
          </div>
        )}
        {requiredLegal && (
          <div className="my-2">
            <Controller
              name="requiredLegal"
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => {
                return (
                  <div>
                    <label
                      className={`cursor-pointer inline-block text-gray-600 font-regular ${
                        error ? 'text-red-600' : ''
                      }`}
                      htmlFor="senderIsNotReceiver"
                    >
                      <input
                        className={`mr-2 inline-block ${error ? 'border-red-600' : ''}`}
                        type="checkbox"
                        value="senderIsNotReceiver"
                        id="senderIsNotReceiver"
                        checked={value}
                        onChange={(e) => {
                          onChange(e.target.checked);
                        }}
                      />
                      <Trans
                        i18nKey="consents-smart-colis"
                        components={{
                          custom: (
                            <a
                              href={`https://smartcolis.com/end-user-terms`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-primary underline"
                            />
                          ),
                        }}
                      />
                    </label>
                    <div>
                      {error && (
                        <div className="text-red-500 mt-1">
                          <FormsErrorParser error={error} />
                        </div>
                      )}
                    </div>
                  </div>
                );
              }}
            />
          </div>
        )}

        {children}

        {!isPending ? (
          <FooterActions nextLabel="global.next" previewLabel="global.navigation-back" onPreview={onPreview} />
        ) : (
          <div className="my-1 flex justify-center items-center">
            <CircleLoader />
          </div>
        )}
      </form>
    </div>
  );
};
